// ---------------------------------------------------------------------------
// What a toast is, and who is holding one.
//
// The queue in `gameStore` is something any part of the socket layer can push
// onto, and for most of this game's life the thing pushed was a bare sentence.
// That is still allowed, and `normalizeToast` is the seam that makes it so: a
// caller with nothing to say about tone says nothing, and gets the neutral one.
//
// Tone is not decoration. "Susie joined" and "You were removed from the room"
// arrived as identical grey cards, so the one that mattered looked like the one
// that did not. Three is deliberately as many as there are: a scale with five
// steps on it is one nobody can read at a glance mid-round.
//
// The bottom half of this file is the ledger that keeps `react-hot-toast` from
// becoming a second source of truth. See `dropQueuedToasts`.
// ---------------------------------------------------------------------------

import { toast } from 'react-hot-toast';

export type ToastTone = 'info' | 'good' | 'alert';

export interface ToastNotice {
  message: string;
  tone: ToastTone;
}

/**
 * The queue in `gameStore`. A bare sentence is still a legal entry, and most of
 * what this game says is one, so a caller with nothing to say about tone is not
 * forced to invent one.
 */
export type ToastQueue = Array<string | ToastNotice>;

/** A queue entry, however it was pushed, as a notice. */
export function normalizeToast(entry: string | ToastNotice): ToastNotice {
  return typeof entry === 'string' ? { message: entry, tone: 'info' } : entry;
}

/**
 * The one card that is not a line from a room.
 *
 * A fixed id so re-announcing a restart replaces the notice rather than
 * stacking a second one, and so the linger timer has something to dismiss.
 */
export const MAINTENANCE_TOAST_ID = 'maintenance';

/**
 * Every card raised off `gameStore.toasts`, oldest first.
 *
 * The library keeps its own list of what is on screen, which is the thing the
 * old hand-rolled host was written to avoid. This ledger is what stops that
 * list becoming a second source of truth: `gameStore.clear()` wipes the queue
 * when a player is kicked or banned, and without this the cards from the room
 * they were just thrown out of would sit there through the dead-end screen.
 *
 * It holds only the room's own cards, so the maintenance notice — which
 * survives `clear()` on purpose, because a restart is a fact about the server
 * rather than about the room — is never in it.
 */
const fromQueue = new Set<string>();

/**
 * Record a card the store raised, and retire the oldest once there are more
 * than `maxVisible` of them. Older ones drop off the top rather than pushing
 * the board off the screen.
 */
export function trackQueuedToast(id: string, maxVisible: number): void {
  fromQueue.add(id);
  while (fromQueue.size > maxVisible) {
    const oldest: string = fromQueue.values().next().value as string;
    fromQueue.delete(oldest);
    // Dismiss rather than remove: it leaves on the transform layer like any
    // other card, so the ones under it do not jump.
    toast.dismiss(oldest);
  }
}

/**
 * Take every card the room raised off the screen at once, with no exit.
 *
 * Called from `gameStore.clear()`. There is no animation because there is no
 * longer a screen for it to animate over: being kicked replaces the room with
 * the dead-end card in the same commit.
 */
export function dropQueuedToasts(): void {
  for (const id of fromQueue) toast.remove(id);
  fromQueue.clear();
}
